import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

interface Message {
  message_id: string;
  direction: 'inbound' | 'outbound';
  author: 'customer' | 'ai' | 'merchant' | 'system';
  type: string;
  text: string | null;
  media: unknown;
  status: string;
  created_at: string;
}

export function ConversationView() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const api = useApi();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: conv } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.get<Record<string, unknown>>(`/conversations/${conversationId}`),
    enabled: !!conversationId,
  });

  const { data: messages } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () => api.get<{ items: Message[] }>(`/conversations/${conversationId}/messages?limit=200`),
    enabled: !!conversationId,
    refetchInterval: 5_000,
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const sendMutation = useMutation({
    mutationFn: (text: string) => api.post(`/conversations/${conversationId}/messages`, { text }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['messages', conversationId] });
    },
  });

  const aiMutation = useMutation({
    mutationFn: (action: 'takeover' | 'resume' | 'disable') =>
      api.post(`/conversations/${conversationId}/ai-control`, { action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  const aiStatus = (conv?.ai_status as string) ?? 'active';

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-slate-200 bg-white px-6 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{(conv?.customer_name as string) ?? (conv?.customer_phone as string) ?? 'Conversation'}</div>
          <div className="text-xs text-slate-500">{(conv?.channel_type as string)?.toUpperCase()} · {String(conv?.customer_phone ?? '')}</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {aiStatus === 'active' ? (
            <button onClick={() => aiMutation.mutate('takeover')} className="rounded-md border border-slate-300 px-3 py-1.5 hover:bg-slate-50">
              Take over
            </button>
          ) : (
            <>
              <button onClick={() => aiMutation.mutate('resume')} className="rounded-md bg-brand-600 text-white px-3 py-1.5 hover:bg-brand-700">
                Resume AI
              </button>
              <button onClick={() => aiMutation.mutate('disable')} className="rounded-md border border-slate-300 px-3 py-1.5 hover:bg-slate-50">
                Disable AI
              </button>
            </>
          )}
        </div>
      </header>
      <div ref={scrollRef} className="flex-1 overflow-auto px-6 py-4 space-y-2 bg-slate-50">
        {(messages?.items ?? []).map((m) => (
          <MessageBubble key={m.message_id} m={m} />
        ))}
      </div>
      <footer className="border-t border-slate-200 bg-white p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) sendMutation.mutate(draft.trim());
          }}
          className="flex items-end gap-2"
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type a reply…"
            rows={2}
            className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (draft.trim()) sendMutation.mutate(draft.trim());
              }
            }}
          />
          <button
            type="submit"
            disabled={!draft.trim() || sendMutation.isPending}
            className="rounded-md bg-brand-600 text-white px-4 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </footer>
    </div>
  );
}

function MessageBubble({ m }: { m: Message }) {
  const isOutbound = m.direction === 'outbound';
  const authorLabel =
    m.author === 'ai' ? 'AI' : m.author === 'merchant' ? 'You' : m.author === 'system' ? 'System' : '';
  return (
    <div className={clsx('flex', isOutbound ? 'justify-end' : 'justify-start')}>
      <div className={clsx('max-w-[70%] rounded-lg px-3 py-2 text-sm shadow-sm', isOutbound ? 'bg-brand-600 text-white' : 'bg-white border border-slate-200')}>
        {authorLabel && isOutbound && (
          <div className="text-xs opacity-75">{authorLabel}</div>
        )}
        <div className="whitespace-pre-wrap">{m.text ?? `[${m.type}]`}</div>
        <div className={clsx('mt-1 text-[10px]', isOutbound ? 'text-white/70' : 'text-slate-400')}>
          {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })} · {m.status}
        </div>
      </div>
    </div>
  );
}
